/**
 * Login Page
 * User authentication page with child-friendly design
 */

import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { motion } from 'framer-motion';
import { Eye, EyeOff, Sparkles } from 'lucide-react';
import { useAuthStore } from '../hooks/useAuthStore';
import { useSoundEffects } from '../hooks/useSoundEffects';
import { apiClient, getErrorMessage } from '../services/api';
import { childService } from '../services';
import { isFirstSessionDone } from './onboarding/FirstSessionPage';
import MindoraCharacter from '../components/character/MindoraCharacter';

const loginSchema = z.object({
  email: z.string().email('Please enter a valid email'),
  password: z.string().min(1, 'Password is required'),
});

type LoginForm = z.infer<typeof loginSchema>;

export default function LoginPage() {
  const navigate = useNavigate();
  const { login, setLoading } = useAuthStore();
  const { play } = useSoundEffects();
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginForm) => {
    try {
      setError('');
      setLoading(true);

      const response = await apiClient.post('/auth/login', data);
      const result = response.data;

      if (!result.success) {
        throw new Error(result.error?.message || 'Login failed');
      }

      login(result.data.user, result.data.accessToken, result.data.refreshToken);
      play('success');

      // Smart redirect based on role and onboarding state
      if (result.data.user.role === 'parent' || result.data.user.role === 'admin') {
        try {
          const children = await childService.getChildren();
          navigate(children.length === 0 ? '/parent/setup-child' : '/parent');
        } catch {
          navigate('/parent');
        }
      } else {
        navigate(isFirstSessionDone(result.data.user.id) ? '/learn' : '/learn/first-session');
      }
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="h-screen flex overflow-hidden">
      {/* Left Side - Decorative */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-kid-ocean via-kid-violet to-kid-berry relative overflow-hidden">
        {/* Background decorations */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-20 h-20 rounded-full bg-white animate-float" />
          <div className="absolute top-40 right-20 w-16 h-16 rounded-full bg-white animate-float-delayed" />
          <div className="absolute bottom-40 left-20 w-12 h-12 rounded-full bg-white animate-float" />
          <div className="absolute bottom-20 right-40 w-8 h-8 rounded-full bg-white animate-float-delayed" />
        </div>

        <div className="flex flex-col items-center justify-center w-full p-12 z-10">
          <motion.img
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            src="/image.png"
            alt="Mindora - Smart Learning for Kids"
            className="max-w-md w-full rounded-3xl shadow-2xl"
          />
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mt-8 text-center text-white"
          >
            <h2 className="text-3xl font-bold mb-2">Welcome to Mindora!</h2>
            <p className="text-lg text-white/80">Where learning becomes an adventure</p>
          </motion.div>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 bg-kid-bg">
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="w-full max-w-md"
        >
          {/* Mascot on mobile */}
          <div className="lg:hidden flex justify-center mb-4">
            <MindoraCharacter mood="waving" size="lg" />
          </div>

          {/* Logo and Title */}
          <div className="text-center mb-8">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', delay: 0.2 }}
              className="inline-block mb-4"
            >
              <img src="/logo.png" alt="Mindora" className="w-24 h-24 object-contain mx-auto" />
            </motion.div>
            <p className="text-gray-600 flex items-center justify-center gap-2">
              <Sparkles className="w-4 h-4 text-kid-tangerine" />
              Learn with Fun!
              <Sparkles className="w-4 h-4 text-kid-tangerine" />
            </p>
          </div>

          {/* Login Card */}
          <div className="bg-white rounded-kid shadow-kid-md p-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">
              Welcome Back!
            </h2>

            {error && (
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                className="mb-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded-xl text-sm"
              >
                {error}
              </motion.div>
            )}

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  {...register('email')}
                  className="input-kid"
                  placeholder="parent@example.com"
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-500">{errors.email.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    {...register('password')}
                    className="input-kid pr-12"
                    placeholder="Enter your password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 p-2 rounded-lg touch-slop"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {errors.password && (
                  <p className="mt-1 text-sm text-red-500">{errors.password.message}</p>
                )}
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full btn-kid-ocean disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Signing in...' : 'Sign In'}
              </button>
            </form>

            <div className="mt-4 text-center">
              <Link to="/forgot-password" className="text-sm text-gray-500 hover:text-kid-purple">
                Forgot your password?
              </Link>
            </div>

            <div className="mt-4 text-center text-sm text-gray-600">
              Don't have an account?{' '}
              <Link to="/register" className="text-kid-ocean font-semibold hover:underline touch-slop inline-block py-1 px-1">
                Sign Up
              </Link>
            </div>
          </div>

          {/* Footer */}
          <p className="text-center text-gray-500 text-sm mt-6">
            Safe and fun learning for children ages 5-18
          </p>
        </motion.div>
      </div>

      {/* Mobile banner - shown only on small screens */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-20px); }
        }
        .animate-float {
          animation: float 4s ease-in-out infinite;
        }
        .animate-float-delayed {
          animation: float 4s ease-in-out infinite;
          animation-delay: 2s;
        }
      `}</style>
    </div>
  );
}
